import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import {
    Search,
    Clock,
    Users,
    ChefHat,
    Star,
    Filter,
    X,
    Grid3X3,
    List,
    BookOpen,
    SlidersHorizontal,
    Flame,
    Timer,
    UtensilsCrossed,
    Tag,
    Heart,
    Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import toast from 'react-hot-toast';
import {
    useRecipes,
    useRecipeCategories,
    useNutritionCategories,
    useSavedRecipeIds,
    useSaveRecipe,
    useSavedRecipes,
} from '@/services/recipes';
import { cn } from '@/lib/utils';
import { RecipeImage } from '@/components/RecipeImage';

const DIFFICULTY_LEVELS = [
    { value: 'EASY', label: 'Easy', color: 'bg-brand-fill-green text-brand-fill-ink' },
    { value: 'MEDIUM', label: 'Medium', color: 'bg-brand-fill-yellow text-brand-fill-ink' },
    { value: 'HARD', label: 'Hard', color: 'bg-brand-fill-orange text-brand-fill-ink' },
];

const PREP_TIME_OPTIONS = [
    { value: 15, label: '15 min or less' },
    { value: 30, label: '30 min or less' },
    { value: 45, label: '45 min or less' },
    { value: 60, label: '1 hour or less' },
];

type ViewMode = 'grid' | 'list';
type SortOption = 'created' | 'name' | 'rating' | 'prepTime';
type TabMode = 'all' | 'saved';

/** The library opens A to Z by title so clients can scan for a recipe. */
const DEFAULT_SORT: SortOption = 'name';

const Recipes = () => {
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [viewMode, setViewMode] = useState<ViewMode>('grid');
    const [sortBy, setSortBy] = useState<SortOption>(DEFAULT_SORT);
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const [savingRecipeId, setSavingRecipeId] = useState<string | null>(null);
    const [tabMode, setTabMode] = useState<TabMode>('all');

    // Filter states
    const [selectedNutritionCategories, setSelectedNutritionCategories] = useState<string[]>([]);
    const [selectedDifficulty, setSelectedDifficulty] = useState<string | undefined>();
    const [maxPrepTime, setMaxPrepTime] = useState<number | undefined>();
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

    const limit = viewMode === 'grid' ? 12 : 10;

    // Fetch recipe categories (meal types)
    const { data: categoriesData } = useRecipeCategories();
    const recipeCategories = categoriesData || [];

    // Fetch nutrition/points categories (the coach-tagged Category system)
    const { data: nutritionCategoriesData } = useNutritionCategories();
    const nutritionCategories = nutritionCategoriesData || [];

    // Fetch saved recipe IDs for showing save status
    const { data: savedRecipeIds } = useSavedRecipeIds();
    const saveRecipeMutation = useSaveRecipe();

    const handleQuickSave = async (e: React.MouseEvent, recipeId: string) => {
        e.preventDefault(); // Prevent navigation to recipe detail
        e.stopPropagation();
        setSavingRecipeId(recipeId);
        try {
            const result = await saveRecipeMutation.mutateAsync(recipeId);
            toast.success(result.saved ? 'Recipe saved!' : 'Recipe removed from saved');
        } catch {
            toast.error('Failed to update saved status');
        } finally {
            setSavingRecipeId(null);
        }
    };

    // Fetch all recipes (when in "all" tab)
    const {
        data: allRecipesData,
        isLoading: isLoadingAll,
        error: errorAll,
    } = useRecipes({
        search: search || undefined,
        page,
        limit,
        categories: selectedCategories.length > 0 ? selectedCategories : undefined,
        nutritionCategories:
            selectedNutritionCategories.length > 0 ? selectedNutritionCategories : undefined,
        difficulty: selectedDifficulty,
        maxPrepTime,
        sortBy,
        sortOrder: sortBy === 'name' ? 'asc' : 'desc',
    });

    // Fetch saved recipes (when in "saved" tab)
    const {
        data: savedRecipesData,
        isLoading: isLoadingSaved,
        error: errorSaved,
    } = useSavedRecipes(page, limit);

    // Use appropriate data based on tab
    const data = tabMode === 'saved' ? savedRecipesData : allRecipesData;
    const isLoading = tabMode === 'saved' ? isLoadingSaved : isLoadingAll;
    const error = tabMode === 'saved' ? errorSaved : errorAll;

    const recipes = data?.recipes || [];
    const pagination = data?.pagination;
    const totalPages = pagination?.totalPages || 1;

    // Reset page when filters or tab change
    useEffect(() => {
        setPage(1);
    }, [
        search,
        selectedNutritionCategories,
        selectedDifficulty,
        maxPrepTime,
        selectedCategories,
        sortBy,
        tabMode,
    ]);

    const hasActiveFilters =
        selectedNutritionCategories.length > 0 ||
        selectedDifficulty ||
        maxPrepTime ||
        selectedCategories.length > 0;

    const clearFilters = () => {
        setSelectedNutritionCategories([]);
        setSelectedDifficulty(undefined);
        setMaxPrepTime(undefined);
        setSelectedCategories([]);
    };

    const toggleCategory = (categoryId: string) => {
        setSelectedCategories((prev) =>
            prev.includes(categoryId)
                ? prev.filter((id) => id !== categoryId)
                : [...prev, categoryId],
        );
    };

    const toggleNutritionCategory = (categoryId: string) => {
        setSelectedNutritionCategories((prev) =>
            prev.includes(categoryId)
                ? prev.filter((id) => id !== categoryId)
                : [...prev, categoryId],
        );
    };

    const activeFilterCount = [
        selectedNutritionCategories.length > 0 ? 1 : 0,
        selectedDifficulty,
        maxPrepTime,
        selectedCategories.length > 0 ? 1 : 0,
    ].filter(Boolean).length;

    if (error) {
        return (
            <div className="max-w-7xl mx-auto px-6 py-8">
                <div className="text-center py-12 bg-card rounded-3xl shadow-sm">
                    <ChefHat className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                        Unable to load recipes
                    </h3>
                    <p className="text-muted-foreground mb-4">Please try refreshing the page.</p>
                    <Button onClick={() => window.location.reload()}>Refresh Page</Button>
                </div>
            </div>
        );
    }

    // Filter Sidebar Component
    const FilterSidebar = ({ mobile = false }: { mobile?: boolean }) => (
        <div
            className={cn(
                'bg-card rounded-2xl shadow-sm border border-border',
                mobile ? 'p-4' : 'p-5 sticky top-4',
            )}
        >
            <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                    <SlidersHorizontal className="w-5 h-5 text-brand-text-blue" />
                    <h3 className="font-semibold text-foreground">Filters</h3>
                </div>
                {hasActiveFilters && (
                    <button
                        onClick={clearFilters}
                        className="text-sm text-brand-text-blue  font-medium"
                    >
                        Clear all
                    </button>
                )}
            </div>

            {/* Meal Type (Categories) */}
            {recipeCategories.length > 0 && (
                <div className="mb-6">
                    <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                        <Tag className="w-4 h-4" />
                        Meal Type
                    </h4>
                    <div className="flex flex-wrap gap-2">
                        {recipeCategories.map((category) => (
                            <button
                                key={category.id}
                                onClick={() => toggleCategory(category.id)}
                                className={cn(
                                    'px-3 py-1.5 rounded-full text-sm font-medium transition-all',
                                    selectedCategories.includes(category.id)
                                        ? 'bg-brand-header-wash text-brand-text-blue border-2 border-brand-header'
                                        : 'bg-muted text-foreground border border-border hover:border-brand-header',
                                )}
                            >
                                {category.icon && <span className="mr-1">{category.icon}</span>}
                                {category.name}
                                {category._count?.recipes > 0 && (
                                    <span className="ml-1 text-xs">
                                        ({category._count.recipes})
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Categories (nutrition/points categories assigned to recipes) */}
            {nutritionCategories.length > 0 && (
                <div className="mb-6">
                    <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                        <UtensilsCrossed className="w-4 h-4" />
                        Categories
                    </h4>
                    <div className="flex flex-wrap gap-2">
                        {nutritionCategories.map((category) => (
                            <button
                                key={category.id}
                                onClick={() => toggleNutritionCategory(category.id)}
                                className={cn(
                                    'px-3 py-1.5 rounded-full text-sm font-medium transition-all',
                                    selectedNutritionCategories.includes(category.id)
                                        ? 'bg-brand-header-wash text-brand-text-blue border-2 border-brand-header'
                                        : 'bg-muted text-foreground border border-border hover:border-brand-header',
                                )}
                            >
                                {category.name}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Difficulty */}
            <div className="mb-6">
                <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                    <Flame className="w-4 h-4" />
                    Difficulty
                </h4>
                <div className="flex flex-wrap gap-2">
                    {DIFFICULTY_LEVELS.map((level) => (
                        <button
                            key={level.value}
                            onClick={() =>
                                setSelectedDifficulty(
                                    selectedDifficulty === level.value ? undefined : level.value,
                                )
                            }
                            className={cn(
                                'px-4 py-2 rounded-lg text-sm font-medium transition-all border',
                                selectedDifficulty === level.value
                                    ? level.color + ' border-2'
                                    : 'bg-muted text-foreground border-border hover:border-brand-header',
                            )}
                        >
                            {level.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Prep Time */}
            <div className="mb-6">
                <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                    <Timer className="w-4 h-4" />
                    Prep Time
                </h4>
                <div className="space-y-2">
                    {PREP_TIME_OPTIONS.map((option) => (
                        <button
                            key={option.value}
                            onClick={() =>
                                setMaxPrepTime(
                                    maxPrepTime === option.value ? undefined : option.value,
                                )
                            }
                            className={cn(
                                'w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-all text-left flex items-center justify-between',
                                maxPrepTime === option.value
                                    ? 'bg-brand-header-wash text-brand-text-blue border-2 border-brand-header'
                                    : 'bg-muted text-foreground border border-border hover:border-brand-header',
                            )}
                        >
                            <span>{option.label}</span>
                            <Clock className="w-4 h-4" />
                        </button>
                    ))}
                </div>
            </div>

            {/* Quick Stats */}
            {pagination && (
                <div className="pt-4 border-t border-border">
                    <p className="text-sm text-muted-foreground">
                        <span className="font-semibold text-foreground">{pagination.total}</span>{' '}
                        recipes found
                    </p>
                </div>
            )}
        </div>
    );

    return (
        <div className="min-h-screen bg-brand-page">
            {/* Hero Header */}
            <div className="bg-brand-header text-brand-header-ink">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
                    <div className="flex items-center gap-3 mb-3">
                        <BookOpen className="w-8 h-8 opacity-80" />
                        <h1 className="text-3xl sm:text-4xl font-bold">Recipe Book</h1>
                    </div>
                    <p className="text-brand-header-ink/80 text-lg max-w-2xl">
                        Discover delicious, healthy recipes crafted for your wellness journey.
                    </p>

                    {/* Tab Toggle */}
                    <div className="flex gap-2 mt-6">
                        <button
                            onClick={() => setTabMode('all')}
                            className={cn(
                                'px-4 py-2 rounded-lg font-medium text-sm transition-all',
                                tabMode === 'all'
                                    ? 'bg-brand-surface text-brand-text-blue'
                                    : 'bg-black/15 text-brand-header-ink hover:bg-black/25',
                            )}
                        >
                            <BookOpen className="w-4 h-4 inline-block mr-2" />
                            All Recipes
                        </button>
                        <button
                            onClick={() => setTabMode('saved')}
                            className={cn(
                                'px-4 py-2 rounded-lg font-medium text-sm transition-all',
                                tabMode === 'saved'
                                    ? 'bg-brand-surface text-brand-text-blue'
                                    : 'bg-black/15 text-brand-header-ink hover:bg-black/25',
                            )}
                        >
                            <Heart
                                className={cn(
                                    'w-4 h-4 inline-block mr-2',
                                    tabMode === 'saved' && 'fill-current',
                                )}
                            />
                            Saved
                            {savedRecipeIds && savedRecipeIds.size > 0 && (
                                <span
                                    className={cn(
                                        'ml-2 px-2 py-0.5 rounded-full text-xs',
                                        tabMode === 'saved'
                                            ? 'bg-brand-header-wash text-brand-text-blue'
                                            : 'bg-black/20 text-brand-header-ink',
                                    )}
                                >
                                    {savedRecipeIds.size}
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
                {/* Search Bar - Floating */}
                <div className="bg-card rounded-2xl shadow-lg p-4 -mt-8 relative z-10 mb-6">
                    <div className="flex flex-col sm:flex-row gap-3">
                        {/* Search Input */}
                        <div className="flex-1 relative">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                            <input
                                type="text"
                                placeholder="Search recipes, ingredients..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full pl-12 pr-10 py-3 rounded-xl border border-border focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent text-foreground placeholder:text-muted-foreground"
                            />
                            {search && (
                                <button
                                    onClick={() => setSearch('')}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>

                        {/* Mobile Filter Toggle */}
                        <Button
                            variant="outline"
                            onClick={() => setShowMobileFilters(!showMobileFilters)}
                            className="sm:hidden flex items-center gap-2"
                        >
                            <Filter className="w-4 h-4" />
                            Filters
                            {activeFilterCount > 0 && (
                                <span className="ml-1 px-2 py-0.5 bg-brand-header-wash text-brand-text-blue rounded-full text-xs font-semibold">
                                    {activeFilterCount}
                                </span>
                            )}
                        </Button>

                        {/* Sort & View Controls */}
                        <div className="flex gap-2">
                            <select
                                aria-label="Sort recipes"
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value as SortOption)}
                                className="px-4 py-3 rounded-xl border border-border focus:outline-none focus:ring-2 focus:ring-ring bg-card text-foreground text-sm font-medium"
                            >
                                <option value="name">A to Z</option>
                                <option value="created">Newest</option>
                                <option value="rating">Top Rated</option>
                                <option value="prepTime">Quick & Easy</option>
                            </select>

                            {/* View Toggle */}
                            <div className="hidden sm:flex border border-border rounded-xl overflow-hidden">
                                <button
                                    onClick={() => setViewMode('grid')}
                                    className={cn(
                                        'p-3 transition-colors',
                                        viewMode === 'grid'
                                            ? 'bg-brand-header-wash text-brand-text-blue'
                                            : 'text-muted-foreground hover:text-foreground',
                                    )}
                                >
                                    <Grid3X3 className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => setViewMode('list')}
                                    className={cn(
                                        'p-3 transition-colors',
                                        viewMode === 'list'
                                            ? 'bg-brand-header-wash text-brand-text-blue'
                                            : 'text-muted-foreground hover:text-foreground',
                                    )}
                                >
                                    <List className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Mobile Filters */}
                    {showMobileFilters && (
                        <div className="mt-4 pt-4 border-t border-border sm:hidden">
                            <FilterSidebar mobile />
                        </div>
                    )}
                </div>

                {/* Main Content Area */}
                <div className="flex gap-6">
                    {/* Desktop Sidebar */}
                    <aside className="hidden lg:block w-72 shrink-0">
                        <FilterSidebar />
                    </aside>

                    {/* Recipe Grid/List */}
                    <main className="flex-1 min-w-0">
                        {/* Active Filters Pills */}
                        {hasActiveFilters && (
                            <div className="flex flex-wrap items-center gap-2 mb-4">
                                <span className="text-sm text-muted-foreground">Filtering by:</span>
                                {selectedNutritionCategories.map((categoryId) => {
                                    const category = nutritionCategories.find(
                                        (c) => c.id === categoryId,
                                    );
                                    if (!category) return null;
                                    return (
                                        <span
                                            key={categoryId}
                                            className="inline-flex items-center gap-1 px-3 py-1 bg-brand-header-wash text-brand-text-blue rounded-full text-sm"
                                        >
                                            {category.name}
                                            <button
                                                onClick={() => toggleNutritionCategory(categoryId)}
                                                className="ml-1 hover:text-brand-text-blue"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    );
                                })}
                                {selectedDifficulty && (
                                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-brand-header-wash text-brand-text-blue rounded-full text-sm">
                                        {selectedDifficulty}
                                        <button
                                            onClick={() => setSelectedDifficulty(undefined)}
                                            className="ml-1 hover:text-brand-text-blue"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                )}
                                {maxPrepTime && (
                                    <span className="inline-flex items-center gap-1 px-3 py-1 bg-brand-header-wash text-brand-text-blue rounded-full text-sm">
                                        ≤{maxPrepTime} min
                                        <button
                                            onClick={() => setMaxPrepTime(undefined)}
                                            className="ml-1 hover:text-brand-text-blue"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                )}
                                {selectedCategories.map((categoryId) => {
                                    const category = recipeCategories.find(
                                        (c) => c.id === categoryId,
                                    );
                                    if (!category) return null;
                                    return (
                                        <span
                                            key={categoryId}
                                            className="inline-flex items-center gap-1 px-3 py-1 bg-brand-header-wash text-brand-text-blue rounded-full text-sm"
                                        >
                                            {category.icon && <span>{category.icon}</span>}
                                            {category.name}
                                            <button
                                                onClick={() => toggleCategory(categoryId)}
                                                className="ml-1 hover:text-brand-text-blue"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    );
                                })}
                            </div>
                        )}

                        {/* Results Count */}
                        {pagination && !isLoading && (
                            <p className="text-sm text-muted-foreground mb-4">
                                {pagination.total === 0 ? (
                                    'No recipes found'
                                ) : (
                                    <>
                                        Showing {(page - 1) * limit + 1}-
                                        {Math.min(page * limit, pagination.total)} of{' '}
                                        {pagination.total} recipes
                                    </>
                                )}
                            </p>
                        )}

                        {/* Loading State */}
                        {isLoading ? (
                            <div
                                className={cn(
                                    viewMode === 'grid'
                                        ? 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5'
                                        : 'space-y-4',
                                )}
                            >
                                {[...Array(6)].map((_, i) => (
                                    <div
                                        key={i}
                                        className="bg-card rounded-2xl shadow-sm overflow-hidden animate-pulse"
                                    >
                                        {viewMode === 'grid' ? (
                                            <>
                                                <div className="aspect-4/3 bg-muted" />
                                                <div className="p-5">
                                                    <div className="h-5 bg-muted rounded w-3/4 mb-3" />
                                                    <div className="h-4 bg-muted rounded w-full mb-2" />
                                                    <div className="h-4 bg-muted rounded w-2/3" />
                                                </div>
                                            </>
                                        ) : (
                                            <div className="flex gap-4 p-4">
                                                <div className="w-32 h-24 bg-muted rounded-xl shrink-0" />
                                                <div className="flex-1">
                                                    <div className="h-5 bg-muted rounded w-1/2 mb-2" />
                                                    <div className="h-4 bg-muted rounded w-full mb-2" />
                                                    <div className="h-4 bg-muted rounded w-1/3" />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : recipes.length === 0 ? (
                            /* Empty State */
                            <div className="text-center py-16 bg-card rounded-3xl shadow-sm">
                                <div
                                    className={cn(
                                        'w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6',
                                        tabMode === 'saved'
                                            ? 'bg-brand-fill-orange'
                                            : 'bg-brand-fill-green',
                                    )}
                                >
                                    {tabMode === 'saved' ? (
                                        <Heart className="w-10 h-10 text-brand-fill-ink" />
                                    ) : (
                                        <ChefHat className="w-10 h-10 text-brand-fill-ink" />
                                    )}
                                </div>
                                <h3 className="text-xl font-semibold text-foreground mb-2">
                                    {tabMode === 'saved'
                                        ? 'No saved recipes yet'
                                        : search || hasActiveFilters
                                          ? 'No recipes match your criteria'
                                          : 'No recipes available'}
                                </h3>
                                <p className="text-muted-foreground max-w-md mx-auto mb-6">
                                    {tabMode === 'saved'
                                        ? "Save your favourite recipes by clicking the heart icon, and they'll appear here."
                                        : search || hasActiveFilters
                                          ? 'Try adjusting your search or filters to find more recipes.'
                                          : 'Check back soon for new healthy recipes!'}
                                </p>
                                {tabMode === 'saved' ? (
                                    <Button onClick={() => setTabMode('all')}>
                                        Browse Recipes
                                    </Button>
                                ) : (
                                    (search || hasActiveFilters) && (
                                        <Button
                                            variant="outline"
                                            onClick={() => {
                                                setSearch('');
                                                clearFilters();
                                            }}
                                        >
                                            Clear all filters
                                        </Button>
                                    )
                                )}
                            </div>
                        ) : (
                            <>
                                {/* Grid View */}
                                {viewMode === 'grid' ? (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                                        {recipes.map((recipe) => (
                                            <Link
                                                key={recipe.id}
                                                to={`/recipes/${recipe.id}`}
                                                className="group bg-card rounded-2xl shadow-sm border border-border overflow-hidden hover:shadow-xl hover:border-brand-header transition-all duration-300"
                                            >
                                                {/* Image */}
                                                <div className="aspect-4/3 relative overflow-hidden bg-brand-header-wash">
                                                    {recipe.imageUrl ? (
                                                        <RecipeImage
                                                            src={recipe.imageUrl}
                                                            alt={recipe.name}
                                                            variant="card"
                                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center">
                                                            <ChefHat className="w-16 h-16 text-brand-text-blue" />
                                                        </div>
                                                    )}

                                                    {/* Badges */}
                                                    <div className="absolute top-3 left-3 right-3 flex justify-between items-start">
                                                        {/* Difficulty */}
                                                        {recipe.difficulty && (
                                                            <span
                                                                className={cn(
                                                                    'px-2 py-1 rounded-lg text-xs font-semibold backdrop-blur-sm',
                                                                    recipe.difficulty === 'EASY' &&
                                                                        'bg-brand-fill-green text-brand-fill-ink',
                                                                    recipe.difficulty ===
                                                                        'MEDIUM' &&
                                                                        'bg-brand-fill-yellow text-brand-fill-ink',
                                                                    recipe.difficulty === 'HARD' &&
                                                                        'bg-brand-fill-orange text-brand-fill-ink',
                                                                )}
                                                            >
                                                                {recipe.difficulty}
                                                            </span>
                                                        )}

                                                        <div className="flex items-center gap-2">
                                                            {/* Rating */}
                                                            {!!recipe.averageRating &&
                                                                recipe.averageRating > 0 && (
                                                                    <div className="bg-brand-surface px-2 py-1 rounded-lg flex items-center gap-1 shadow-sm">
                                                                        <Star className="w-4 h-4 text-brand-fill-ink fill-brand-fill-yellow" />
                                                                        <span className="text-sm font-semibold">
                                                                            {recipe.averageRating.toFixed(
                                                                                1,
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                )}

                                                            {/* Save Button */}
                                                            <button
                                                                onClick={(e) =>
                                                                    handleQuickSave(e, recipe.id)
                                                                }
                                                                disabled={
                                                                    savingRecipeId === recipe.id
                                                                }
                                                                className={cn(
                                                                    'w-8 h-8 rounded-full flex items-center justify-center shadow-sm transition-colors',
                                                                    savedRecipeIds?.has(recipe.id)
                                                                        ? 'bg-brand-header text-brand-header-ink hover:bg-brand-header/90'
                                                                        : 'bg-brand-surface text-muted-foreground hover:text-brand-text-blue',
                                                                )}
                                                            >
                                                                {savingRecipeId === recipe.id ? (
                                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                                ) : (
                                                                    <Heart
                                                                        className={cn(
                                                                            'w-4 h-4',
                                                                            savedRecipeIds?.has(
                                                                                recipe.id,
                                                                            ) && 'fill-current',
                                                                        )}
                                                                    />
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Content */}
                                                <div className="p-5">
                                                    <h3 className="font-semibold text-foreground text-lg mb-2 group-hover:text-brand-text-blue transition-colors line-clamp-1">
                                                        {recipe.name}
                                                    </h3>

                                                    {recipe.description && (
                                                        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                                                            {recipe.description}
                                                        </p>
                                                    )}

                                                    {/* Meta Info */}
                                                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                                                        {(recipe.prepTime || recipe.cookTime) && (
                                                            <span className="flex items-center gap-1.5">
                                                                <Clock className="w-4 h-4 text-brand-text-blue" />
                                                                {(recipe.prepTime || 0) +
                                                                    (recipe.cookTime || 0)}{' '}
                                                                min
                                                            </span>
                                                        )}
                                                        {recipe.servings && (
                                                            <span className="flex items-center gap-1.5">
                                                                <Users className="w-4 h-4 text-brand-text-blue" />
                                                                {recipe.servings}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                ) : (
                                    /* List View */
                                    <div className="space-y-3">
                                        {recipes.map((recipe) => (
                                            <Link
                                                key={recipe.id}
                                                to={`/recipes/${recipe.id}`}
                                                className="group flex gap-4 bg-card rounded-xl shadow-sm border border-border p-4 hover:shadow-lg hover:border-brand-header transition-all"
                                            >
                                                {/* Thumbnail */}
                                                <div className="w-32 h-24 rounded-xl overflow-hidden bg-brand-header-wash shrink-0">
                                                    {recipe.imageUrl ? (
                                                        <RecipeImage
                                                            src={recipe.imageUrl}
                                                            alt={recipe.name}
                                                            variant="thumb"
                                                            thumbSize={128}
                                                            thumbAspect={4 / 3}
                                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center">
                                                            <ChefHat className="w-8 h-8 text-brand-text-blue" />
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Content */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-start justify-between gap-2 mb-1">
                                                        <h3 className="font-semibold text-foreground group-hover:text-brand-text-blue transition-colors line-clamp-1">
                                                            {recipe.name}
                                                        </h3>
                                                        <div className="flex items-center gap-2 shrink-0">
                                                            {!!recipe.averageRating &&
                                                                recipe.averageRating > 0 && (
                                                                    <div className="flex items-center gap-1">
                                                                        <Star className="w-4 h-4 text-brand-fill-ink fill-brand-fill-yellow" />
                                                                        <span className="text-sm font-semibold text-foreground">
                                                                            {recipe.averageRating.toFixed(
                                                                                1,
                                                                            )}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            {/* Save Button */}
                                                            <button
                                                                onClick={(e) =>
                                                                    handleQuickSave(e, recipe.id)
                                                                }
                                                                disabled={
                                                                    savingRecipeId === recipe.id
                                                                }
                                                                className={cn(
                                                                    'w-7 h-7 rounded-full flex items-center justify-center transition-colors',
                                                                    savedRecipeIds?.has(recipe.id)
                                                                        ? 'bg-brand-header-wash text-brand-text-blue'
                                                                        : 'bg-muted text-muted-foreground hover:text-brand-text-blue',
                                                                )}
                                                            >
                                                                {savingRecipeId === recipe.id ? (
                                                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                                ) : (
                                                                    <Heart
                                                                        className={cn(
                                                                            'w-3.5 h-3.5',
                                                                            savedRecipeIds?.has(
                                                                                recipe.id,
                                                                            ) && 'fill-current',
                                                                        )}
                                                                    />
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {recipe.description && (
                                                        <p className="text-sm text-muted-foreground line-clamp-1 mb-2">
                                                            {recipe.description}
                                                        </p>
                                                    )}

                                                    {/* Meta */}
                                                    <div className="flex flex-wrap items-center gap-3 text-sm">
                                                        {(recipe.prepTime || recipe.cookTime) && (
                                                            <span className="flex items-center gap-1 text-muted-foreground">
                                                                <Clock className="w-3.5 h-3.5" />
                                                                {(recipe.prepTime || 0) +
                                                                    (recipe.cookTime || 0)}{' '}
                                                                min
                                                            </span>
                                                        )}
                                                        {recipe.difficulty && (
                                                            <span
                                                                className={cn(
                                                                    'px-2 py-0.5 rounded text-xs font-medium',
                                                                    recipe.difficulty === 'EASY' &&
                                                                        'bg-brand-fill-green text-brand-fill-ink',
                                                                    recipe.difficulty ===
                                                                        'MEDIUM' &&
                                                                        'bg-brand-fill-yellow text-brand-fill-ink',
                                                                    recipe.difficulty === 'HARD' &&
                                                                        'bg-brand-fill-orange text-brand-fill-ink',
                                                                )}
                                                            >
                                                                {recipe.difficulty}
                                                            </span>
                                                        )}
                                                        {!!recipe.categories?.length && (
                                                            <span className="text-muted-foreground">
                                                                {recipe.categories
                                                                    .map((c) => c.name)
                                                                    .join(', ')}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                )}

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="flex items-center justify-center gap-2 py-8">
                                        <Button
                                            variant="outline"
                                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                                            disabled={!pagination?.hasPrev}
                                            size="sm"
                                        >
                                            Previous
                                        </Button>

                                        <div className="flex items-center gap-1">
                                            {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                                let pageNum;
                                                if (totalPages <= 5) {
                                                    pageNum = i + 1;
                                                } else if (page <= 3) {
                                                    pageNum = i + 1;
                                                } else if (page >= totalPages - 2) {
                                                    pageNum = totalPages - 4 + i;
                                                } else {
                                                    pageNum = page - 2 + i;
                                                }

                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => setPage(pageNum)}
                                                        className={cn(
                                                            'w-9 h-9 rounded-lg font-medium text-sm transition-colors',
                                                            page === pageNum
                                                                ? 'bg-brand-header text-brand-header-ink'
                                                                : 'text-foreground hover:bg-muted',
                                                        )}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        <Button
                                            variant="outline"
                                            onClick={() =>
                                                setPage((p) => Math.min(totalPages, p + 1))
                                            }
                                            disabled={!pagination?.hasNext}
                                            size="sm"
                                        >
                                            Next
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}
                    </main>
                </div>
            </div>
        </div>
    );
};

export default Recipes;
